import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMyTeams } from '../components/teams/useMyTeams';
import { useHomeScreenTeams } from '../components/teams/useHomeScreenTeams';
import CreateTeamFormModal from '../components/forms/CreateTeamFormModal';
import { useAuth } from '../auth/AuthContext';
import { getTeamLogoUrl } from '../components/teams/utils/teamLogo';
import '../components/teams/teams.css';

export default function Teams() {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { teams, reloadTeams } = useMyTeams();
  const {
    homeTeamIds,
    loading: homeTeamsLoading,
    savingTeamId,
    setTeamVisibleOnHome,
  } = useHomeScreenTeams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [homeToast, setHomeToast] = useState(null);
  const toastTimerRef = useRef(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const handleCreated = (teamId) => {
    reloadTeams();
    closeModal();
    if (teamId) navigate(`/teams/${teamId}`);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (message, tone = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setHomeToast({ message, tone });
    toastTimerRef.current = setTimeout(() => {
      setHomeToast(null);
      toastTimerRef.current = null;
    }, 2600);
  };

  // Memoize a sorted list to avoid unnecessary re-sorts on re-renders.
  const sortedTeams = useMemo(() => {
    if (!teams) return [];
    return [...teams].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [teams]);

  const headerActions = (
    <div className="teams-header-actions split-header" role="group" aria-label="Club actions">
      <Link to="/teams/search" className="button button-secondary">Join a Club</Link>
      <button type="button" onClick={openModal} className="button button-primary">Create a Club</button>
    </div>
  );

  if (authLoading) {
    return (
      <div className="page teams-page">
        <Card title="Your Clubs">
          <p className="text-muted">Loading authentication...</p>
        </Card>
      </div>
    );
  }

  if (!user) {
    const nextPath = `${location.pathname}${location.search || ''}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  if (!sortedTeams || sortedTeams.length === 0) {
    return (
      <div className="page teams-page">
        {headerActions}
        <Card title="Your Clubs">
          <p className="text-muted">You haven't joined or created any clubs yet.</p>
        </Card>

        <CreateTeamFormModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSuccess={handleCreated}
        />
      </div>
    );
  }

  return (
    <div className="page teams-page">
      {headerActions}
      {homeToast && (
        <div
          role="status"
          aria-live="polite"
          className="card"
          style={{
            marginBottom: 12,
            borderColor: homeToast.tone === 'error' ? '#dc2626' : '#16a34a',
            background: homeToast.tone === 'error' ? '#fef2f2' : '#f0fdf4',
          }}
        >
          <div className="card-body" style={{ fontWeight: 600 }}>
            {homeToast.message}
          </div>
        </div>
      )}

      <div className="teams-list">
        {sortedTeams.map(team => {
          const brand = team.brand || {};
          const logoUrl = getTeamLogoUrl(team);
          const brandVars = {
            ...(brand.primaryColor && { '--team-primary': brand.primaryColor }),
            ...(brand.secondaryColor && { '--team-secondary': brand.secondaryColor }),
            ...(brand.headingTextColor && { '--team-heading-text': brand.headingTextColor }),
            ...(brand.bodyTextColor && {
              '--team-body-text': brand.bodyTextColor,
              '--team-text-muted': brand.bodyTextColor,
            }),
          };

          const isShownOnHome = homeTeamIds.includes(team.id);
          const isSaving = savingTeamId === team.id;

          return (
            <Card
              key={team.id}
              className="card compact-card club-list-card"
              style={{ ...brandVars, position: 'relative' }}
            >
              <button
                type="button"
                className={`button ${isShownOnHome ? 'button-primary' : 'button-secondary'} button--xs`}
                disabled={homeTeamsLoading || isSaving}
                onClick={async () => {
                  const result = await setTeamVisibleOnHome(team.id);
                  if (!result?.ok) {
                    showToast(`Could not update "${team.name}" on your home screen.`, 'error');
                    return;
                  }
                  if (result.shown) {
                    showToast(`Added "${team.name}" to your home screen.`);
                  } else {
                    showToast(`Removed "${team.name}" from your home screen.`);
                  }
                }}
                aria-pressed={isShownOnHome}
                aria-label={isShownOnHome ? `Remove ${team.name} from Home Screen` : `Show ${team.name} on Home Screen`}
                title={isShownOnHome ? 'Remove from Home Screen' : 'Show on Home Screen'}
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  width: 28,
                  height: 28,
                  minHeight: 28,
                  padding: 0,
                  borderRadius: 999,
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                {isSaving ? '...' : (isShownOnHome ? '-' : '+')}
              </button>
              <div className={`club-card-heading${logoUrl ? ' club-card-heading--with-logo' : ''}`}>
                {logoUrl && (
                  <div className="club-card-logo">
                    <img
                      src={logoUrl}
                      alt={`${team.name} logo`}
                      onError={(event) => {
                        event.currentTarget.closest('.club-card-logo')?.style.setProperty('display', 'none');
                      }}
                    />
                  </div>
                )}
                <div className="card-title">{team.name}</div>
              </div>
              <div className="card-body">
                <p className="text-muted club-list-card__description">
                  {team.description || 'No description yet.'}
                </p>
                <div className="team-actions">
                  <Link to={`/teams/${team.clubUrlSlug || team.id}`} className="button button-primary">View</Link>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <CreateTeamFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSuccess={handleCreated}
      />
    </div>
  );
}
